import type { EffectiveRole } from '@/lib/types';

/**
 * مصفوفة الصلاحيات — ثابتة بالدور (قرار 8 سبتمبر 2026).
 * المرجع: المستند 05 §13 + الدراسة العميقة §03. التخصيص يكون بالنطاق فقط
 * (track للمشرف الداخلي، الروستر للمعلم، Caseload للمرشد، الأبناء لولي الأمر) داخل الخدمات.
 * أي صلاحية جديدة تُضاف هنا وتُختبر سلباً (رفض) لا إيجاباً فقط.
 */
export const PERMISSIONS = {
  // إدارة المنصة
  'institution.create': ['PLATFORM_ADMIN'],
  'institution.view_all': ['PLATFORM_ADMIN', 'PLATFORM_INSTITUTIONS'],
  'institution.status.change': ['PLATFORM_ADMIN'],
  'regulatory_entity.create': ['PLATFORM_ADMIN'],
  'audit.view_all': ['PLATFORM_ADMIN'],
  'consent_text.manage': ['PLATFORM_ADMIN'],
  // حسابات إدارة المنصة: إنشاءُ ثانيةٍ وثالثة وإسقاطُ الصفة — إدارة المنصة وحدها (18 سبتمبر 2026)
  'platform_admin.manage': ['PLATFORM_ADMIN'],
  // حذفُ الحساب بتعهّد: الطلبُ لكل من دخل بلا صلاحية، والقرارُ لإدارة المنصة وحدها (20 سبتمبر 2026)
  'account_deletion.decide': ['PLATFORM_ADMIN'],
  // 25 سبتمبر 2026 — صندوقُ «تواصل معنا» وطلباتُ العرض (0060)
  'contact.manage': ['PLATFORM_ADMIN', 'PLATFORM_SUPPORT'],
  // 25 سبتمبر 2026 — صحةُ التشغيل: آخرُ نسخةٍ احتياطية والأخطاءُ غير المتوقعة (0061)
  'platform.health.view': ['PLATFORM_ADMIN'],
  /** استبياناتُ الاستعمال (0125): تعريفُها ومجاميعُها لإدارة المنصة — والإجابةُ لكل من استُحقّ له استبيان بلا صلاحية */
  'usability_survey.manage': ['PLATFORM_ADMIN'],
  // 20 أكتوبر 2026 — شركاءُ النجاح في الصفحة الأولى (0102)
  'platform.partners.manage': ['PLATFORM_ADMIN'],
  // 25 سبتمبر 2026 — مراجعةُ البلاغات عن محتوى الأكاديميات (0063)
  'content.moderate': ['PLATFORM_ADMIN', 'PLATFORM_ACADEMIES'],
  // 21 سبتمبر 2026 — دخولُ إدارة المنصة إلى الحسابات التجريبية وحدها (0042)
  'sandbox.enter': ['PLATFORM_ADMIN'],
  // 21 سبتمبر 2026 — الكشف على الحسابات كلِّها (اسمٌ وصفةٌ وحالة — لا ملفات)
  'user.view_all': ['PLATFORM_ADMIN', 'PLATFORM_SUPPORT'],
  // مراجعةُ شهادات المهنيين (0096 — 18 أكتوبر 2026): التوثيقُ يُظهرها في دليل الكوادر
  'credential.review': ['PLATFORM_ADMIN', 'PLATFORM_SUPPORT'],
  // 22 سبتمبر 2026 — تعديلُ حسابٍ يدوياً من إدارة المنصة (الاسم/البريد/الجوال/كلمة مؤقتة) — لا حسابَ إدارةٍ آخر
  'user.manage_all': ['PLATFORM_ADMIN'],
  // قراءة نصوص الموافقات المنشورة — إدارة المنصة تحرّرها، والمؤسسة تقرأها لتعرضها على وليّ الأمر
  'consent_text.view': ['PLATFORM_ADMIN', 'INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR', 'SPECIALIST', 'COUNSELOR'],

  // المؤسسة
  'institution.view': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR', 'SPECIALIST', 'COUNSELOR', 'TEACHER', 'OFFICE_STAFF'],
  'institution.settings.update': ['INSTITUTION_ADMIN'],
  // هوية المؤسسة: الشعار والبطاقة والمعرض والموقع — الإدارة وحدها
  'institution.branding.manage': ['INSTITUTION_ADMIN'],
  // المسمى المهني والتخصص على العضوية — الأخصائي يملك حسابه، والمؤسسة تسمّيه في سياقها
  'membership.profile.update': ['INSTITUTION_ADMIN', 'INSTITUTION_DELEGATE'],
  'audit.view': ['INSTITUTION_ADMIN'],

  // الحسابات (Account Provisioning 3.2)
  'user.create': ['INSTITUTION_ADMIN', 'INSTITUTION_DELEGATE'],
  'user.view': ['INSTITUTION_ADMIN', 'INSTITUTION_DELEGATE'],
  'user.status.change': ['INSTITUTION_ADMIN'],
  /** كلمةٌ مؤقتة لموظفٍ أنشأته المؤسسة (معلّم/مشرف داخلي) ولا صفةَ له خارجها — 23 سبتمبر 2026 */
  'user.password.reset': ['INSTITUTION_ADMIN', 'INSTITUTION_DELEGATE'],
  'membership.end': ['INSTITUTION_ADMIN'],
  // 0078 — المفوَّضُ واحدٌ يعيّنه المدير وحده؛ والمنحُ الفردية من المدير أو المفوَّض
  'membership.delegate.manage': ['INSTITUTION_ADMIN'],
  // 1 نوفمبر 2026 — منصبُ المدير (0121): الرفعُ إلى مدير مساوٍ والإعادةُ والتنازل — للمدير وحده (لا المفوَّض: لا يمنح ما لا يملك)
  'membership.role.change': ['INSTITUTION_ADMIN'],
  'membership.grants.manage': ['INSTITUTION_ADMIN', 'INSTITUTION_DELEGATE'],
  // 0078 — رسائلُ الكادر داخل المؤسسة: كلُّ عضوٍ نشط
  'staff.message': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR', 'SPECIALIST', 'COUNSELOR', 'TEACHER', 'OFFICE_STAFF'],
  'invitation.send': ['INSTITUTION_ADMIN', 'INSTITUTION_DELEGATE'],
  'invitation.view': ['INSTITUTION_ADMIN', 'INSTITUTION_DELEGATE'],
  'professional.search': ['INSTITUTION_ADMIN', 'INSTITUTION_DELEGATE'],
  'staff_credentials.upload': ['INSTITUTION_ADMIN'],
  'staff_credentials.view': ['INSTITUTION_ADMIN'],
  'staff_credentials.remove': ['INSTITUTION_ADMIN'],

  // الطلبة — بابُ الإنشاء من المؤسسة مغلقٌ (0027): الطفل يدخل من حساب وليّ أمره
  // صورة الطالب — رفعها وحذفها؛ وعرضُها يمرّ بنطاق `student.view` وبموافقة PHOTO سارية
  'student.photo.manage': ['INSTITUTION_ADMIN'],
  'student.view': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR', 'SPECIALIST', 'COUNSELOR', 'OFFICE_STAFF'],
  // 0070: أرقامُ تواصل أولياء الأمور (بريدٌ وجوال) في الملف الأساسي للإدارة وحدها — هي من تأخذ الموافقات وتتواصل رسمياً
  'student.guardian_contacts.view': ['INSTITUTION_ADMIN'],
  'student.update': ['INSTITUTION_ADMIN'],
  // طلبات ربط الأطفال: وليّ الأمر يطلب برقم الطفل المرجعي، والمؤسسة تقبل فيلتحق
  'link_request.view': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR'],
  'link_request.decide': ['INSTITUTION_ADMIN'],
  'student.promote_grade': ['INSTITUTION_ADMIN'],
  'special_education_file.create': ['INSTITUTION_ADMIN', 'SPECIALIST'],
  // سجلّات الطالب (0027): ملفان أكاديمي/سلوكي بتاريخ، ومتابعة يومية، ومرفقات بلا حدّ —
  // الإدارة ترفع كما الأخصائي، والقراءة بالإسناد للمختصّ (BR-COUNSEL-001) في الخدمة
  'student_file.view': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR', 'SPECIALIST', 'COUNSELOR'],
  // 30 سبتمبر 2026 (قرار صاحب المنصة): المشرفُ الداخلي يرفع ملفاتَ الخطط الدورية (الأسبوعية والشهرية والفصلية) أيضاً
  'student_file.manage': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR', 'SPECIALIST', 'COUNSELOR'],
  'daily_followup.view': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR', 'SPECIALIST', 'COUNSELOR'],
  'daily_followup.manage': ['INSTITUTION_ADMIN', 'SPECIALIST', 'COUNSELOR'],
  'attachment.view': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR', 'SPECIALIST', 'COUNSELOR'],
  'attachment.manage': ['INSTITUTION_ADMIN', 'SPECIALIST', 'COUNSELOR'],
  // الحقول الإجبارية تضبطها الإدارة صفوفاً، ويقرؤها من يملأ النماذج
  'required_fields.view': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR', 'SPECIALIST', 'COUNSELOR'],
  'required_fields.manage': ['INSTITUTION_ADMIN'],
  // لوحة الإدارة: الأخصائيون → طلبتهم → ما رُفع
  'overview.view': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR'],
  // 0037: خيطٌ ثنائيّ الاتجاه مع وليّ الأمر — كلُّ من يُسنَد إليه الطفل يردّ (المعلّم والمشرف الداخلي معهم)
  'student.message_guardian': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR', 'SPECIALIST', 'COUNSELOR', 'TEACHER', 'OFFICE_STAFF'],
  'guardian.invite': ['INSTITUTION_ADMIN'],
  'student_account.create': ['INSTITUTION_ADMIN'],

  // الموافقات
  'consent.request': ['INSTITUTION_ADMIN'],
  'consent.view': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR', 'SPECIALIST', 'COUNSELOR'],
  'consent.sign': ['PARENT'],
  'consent.revoke': ['PARENT'],
  'consent.view_own': ['PARENT'],

  // ولي الأمر — حسابه وأولاده ووثائقهم بيده (0027)
  'student.view_own_children': ['PARENT'],
  'guardian.self.manage': ['PARENT'],
  'link_request.create': ['PARENT'],
  'guardian_document.manage': ['PARENT'],
  // وثائق الطفل تُقرأ في المؤسسة الملتحق بها ضمن نطاق `student.view`
  // 0070: مربّي الصف يدير وثائقَ طلبته (شهادة الميلاد ودفتر التطعيم) — النطاقُ روستره في الخدمة
  'guardian_document.view': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR', 'SPECIALIST', 'COUNSELOR', 'TEACHER'],

  // الخطط والأهداف (P2) — المستند 05 §6.1 · §7 · الوحدة 9
  'plan.view': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR', 'SPECIALIST', 'COUNSELOR'],
  'plan.create': ['SPECIALIST', 'COUNSELOR'],
  'plan.submit': ['SPECIALIST', 'COUNSELOR'],
  'plan.version.create': ['SPECIALIST', 'COUNSELOR'],
  // 25 أكتوبر 2026 (0112): تعديلُ الخطة والهدف بعد الحفظ — لمن يكتبها ولمن يشرف، وكلُّ تعديلٍ باسم صاحبه في سجلٍّ مجمَّد
  'plan.edit': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR', 'SPECIALIST', 'COUNSELOR'],
  'goal.edit': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR', 'SPECIALIST', 'COUNSELOR'],
  'plan.first_review': ['INTERNAL_SUPERVISOR'],
  // 30 سبتمبر 2026: اعتمادُ الخطط بيد المشرف الداخلي (مدير القسم) أو الإدارة — والعيونُ الأربع في الخدمة (من راجع لا يعتمد)
  'plan.final_approve': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR'],
  'goal.view': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR', 'SPECIALIST', 'COUNSELOR'],
  'goal.create': ['SPECIALIST', 'COUNSELOR'],
  'goal.submit': ['SPECIALIST', 'COUNSELOR'],
  'goal.approve': ['INTERNAL_SUPERVISOR'],
  'goal.mark_criteria_met': ['SPECIALIST', 'COUNSELOR'],
  'goal.criteria.confirm': ['INTERNAL_SUPERVISOR', 'INSTITUTION_ADMIN'],
  // 0048 — تقييمُ الخطة (الأخصائي/المرشد)، وسجلُّ الأداء الحالي، ورأيُ الأسرة بعد إنهاء الخطة، ومشاركةُ الخطة مع المعلّم
  'plan.evaluate': ['SPECIALIST', 'COUNSELOR'],
  'plan.share': ['SPECIALIST', 'COUNSELOR', 'INSTITUTION_ADMIN'],
  'plan.family_review': ['PARENT'],
  'student_performance.view': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR', 'SPECIALIST', 'COUNSELOR', 'TEACHER'],
  'student_performance.manage': ['SPECIALIST', 'COUNSELOR', 'INSTITUTION_ADMIN'],
  'goal.close': ['SPECIALIST', 'COUNSELOR'],
  'goal.request_modification': ['SPECIALIST', 'COUNSELOR'],
  'goal.exception.request': ['SPECIALIST', 'COUNSELOR'],
  'goal.exception.approve': ['INTERNAL_SUPERVISOR'],

  // التحضير والجلسات والتسجيلات (P3) — المستند 05 §6.2 · §8 · §15
  'preparation.view': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR', 'SPECIALIST', 'COUNSELOR'],
  'preparation.manage': ['SPECIALIST', 'COUNSELOR'],
  'session.view': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR', 'SPECIALIST', 'COUNSELOR'],
  // 30 سبتمبر 2026: المشرفُ الداخلي يوزّع الجلسات على الأخصائيين إلكترونياً — والتعارضُ يُرفض في الخدمة
  'session.schedule': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR', 'SPECIALIST', 'COUNSELOR'],
  // 0084 — العطل: إدارةُ المنصة لعطل المملكة، وإدارةُ المؤسسة لعطلها هي
  'holiday.manage': ['PLATFORM_ADMIN', 'INSTITUTION_ADMIN'],
  // 0085 — طلبُ الإجازة لكل الكادر (ومن يقرّر تحدّده سلسلةُ المؤسسة في الخدمة)، وسلاسلُ الموافقة للمدير وحده
  'leave.request': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR', 'SPECIALIST', 'COUNSELOR', 'TEACHER', 'OFFICE_STAFF'],
  // حضورُ الكادر (0098 — 18 أكتوبر 2026): كلُّ موظفٍ يسجّل قدومه وانصرافه ويطلب مغادرة، والإدارةُ تُثبّت وتعتمد وترى التقرير
  'staff_attendance.self': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR', 'SPECIALIST', 'COUNSELOR', 'TEACHER', 'OFFICE_STAFF'],
  'staff_attendance.manage': ['INSTITUTION_ADMIN', 'INSTITUTION_DELEGATE'],
  'approval_policy.manage': ['INSTITUTION_ADMIN'],
  // 0085 — صندوقُ الشكاوى: يقدّمها الكادر، وتقرؤها إدارةُ المؤسسة وحدها (لا الإداريين ولا المفوَّض)، ويكشف المخفيَّ إدارةُ المنصة وحدها
  'staff_complaint.submit': ['INTERNAL_SUPERVISOR', 'SPECIALIST', 'COUNSELOR', 'TEACHER', 'OFFICE_STAFF'],
  'staff_complaint.view': ['INSTITUTION_ADMIN'],
  'staff_complaint.reveal': ['PLATFORM_ADMIN'],
  'session.start': ['SPECIALIST', 'COUNSELOR'],
  'session.end': ['SPECIALIST', 'COUNSELOR'],
  'session.results.record': ['SPECIALIST', 'COUNSELOR'],
  'session.review': ['INTERNAL_SUPERVISOR'],
  'session.cancel': ['INSTITUTION_ADMIN', 'SPECIALIST', 'COUNSELOR'],
  // جدول المواعيد (18 سبتمبر 2026): كلٌّ يرى جدولَه، والإدارة والمشرف يريان جدول أي كادر
  'schedule.view_own': ['SPECIALIST', 'COUNSELOR'],
  'schedule.view': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR'],
  // 0073 — دوامُ الكادر: كلٌّ يضبط دوامَه، والإدارةُ والمشرفُ دوامَ أي كادر ويريان فتحاته الفارغة عند الجدولة
  'staff_availability.manage_own': ['SPECIALIST', 'COUNSELOR'],
  'staff_availability.manage': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR'],
  // 0074 — التدريبُ المنزلي: الأخصائي والمرشد يرسلان الفيديو للأسرة، والإدارةُ والمشرف يقرآن الالتزام
  'home_training.manage': ['SPECIALIST', 'COUNSELOR'],
  'home_training.view': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR', 'SPECIALIST', 'COUNSELOR'],
  // 0075 — المدير يرى عملَ العضو بتفاصيله (يُكتب في سجلّ الوصول الحسّاس)، ودعواتُ أولياء الأمور دفعةً
  'staff_work.view': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR'],
  // 1 نوفمبر 2026 — «عرضٌ بعين العضو» للقراءة (0122): الإدارةُ وحدها
  'staff.view_as': ['INSTITUTION_ADMIN'],
  'guardian_invitation.manage': ['INSTITUTION_ADMIN'],
  'recording.view': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR', 'SPECIALIST', 'COUNSELOR'],
  'recording.upload': ['SPECIALIST', 'COUNSELOR'],
  'recording.access.request': ['PARENT'],
  'recording.exception_approve': ['INSTITUTION_ADMIN'],
  'recording.retention.run': ['INSTITUTION_ADMIN'],
  // 1 نوفمبر 2026 — تحميلُ تسجيل الجلسة (0124): الإدارةُ دائماً، وصاحبُ الجلسة إن لم تطفئه إدارتُه (يُفحص في الخدمة)
  'recording.download': ['INSTITUTION_ADMIN', 'SPECIALIST', 'COUNSELOR'],

  // التكامل مع الجهات الرقابية
  'integration.manage': ['PLATFORM_ADMIN'],
  'integration.view': ['INSTITUTION_ADMIN'],

  // التقارير (P4) — المستند 06 §Reports: الإرجاع للمشرف والاعتماد للإدارة حصراً
  'report.view': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR', 'SPECIALIST', 'COUNSELOR'],
  'report.create': ['SPECIALIST', 'COUNSELOR'],
  'report.submit': ['SPECIALIST', 'COUNSELOR'],
  'report.first_review': ['INTERNAL_SUPERVISOR'],
  'report.return': ['INTERNAL_SUPERVISOR'],
  'report.approve': ['INSTITUTION_ADMIN'],
  'report.publish': ['INSTITUTION_ADMIN'],
  // المنشور وحده — النطاق (أبناؤه هو) يُفرض في الخدمة
  'report.view_published': ['PARENT', 'STUDENT'],

  // الروستر وقوائم الحالات (P4) — BR-COUNSEL-001
  'caseload.view': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR', 'SPECIALIST', 'COUNSELOR'],
  'caseload.assign': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR'],
  'teacher.roster.manage': ['INSTITUTION_ADMIN'],
  'teacher.roster.view': ['TEACHER'],

  // المعلم — الوحدة 25 (المستند 05 §Teacher)
  'teacher.behavior_observation.create': ['TEACHER'],
  'behavior_observation.view': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR', 'SPECIALIST', 'COUNSELOR'],
  // 30 سبتمبر 2026 — قرار صاحب المنصة (يعكس 22 سبتمبر): معلّمُ الصف يُحيل طالبَه إلى الإرشاد أو التربية الخاصة — النطاقُ روستره
  'teacher.referral.create': ['TEACHER'],
  // الخطة المشتركة: هدف حالي واستراتيجية مقترحة فقط — BR-REFERRAL-003
  'teacher.shared_plan.view': ['TEACHER'],

  // الإحالات (P4) — BR-COUNSEL-002 · BR-REFERRAL-001/002
  'referral.view': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR', 'SPECIALIST', 'COUNSELOR'],
  // طلب مباشر من الأهل أو الطالب — يبقى بانتظار قبول المرشد
  'referral.request': ['PARENT', 'STUDENT'],
  // مسار احتياطي لمن لا حساب لمُحيله: الإدارة أو المرشد يُدخلها نيابةً باسم حر
  'referral.create_on_behalf': ['INSTITUTION_ADMIN', 'COUNSELOR'],
  'referral.decide': ['SPECIALIST', 'INSTITUTION_ADMIN', 'COUNSELOR'],

  // محرّك القواعد والتنبيهات (P5) — المستند 05 §8 · المستند 06 §4.6
  'rule.view': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR', 'PLATFORM_ADMIN'],
  'rule.configure': ['INSTITUTION_ADMIN'],
  // البذر يكتب صفوف منصة بتجاوز العزل — فعلُ إدارة منصة لا إعدادُ مؤسسة
  'rule.seed': ['PLATFORM_ADMIN'],
  'rule.run': ['INSTITUTION_ADMIN'],
  'alert.view': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR', 'SPECIALIST', 'COUNSELOR'],
  'alert.assign': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR'],
  // الإغلاق مسموح بالدور، والتنبيه الحرج تُقيَّده الخدمة بإدارة المؤسسة وحدها
  'alert.resolve': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR', 'SPECIALIST', 'COUNSELOR'],

  // المؤشرات (P5) — المستند 05 §9 · §10
  'indicator.view': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR'],
  'indicator.configure': ['INSTITUTION_ADMIN'],
  'indicator.recalculate': ['INSTITUTION_ADMIN'],

  // الدرجة المركّبة للمهني (P6) — رقمٌ على شخص، فصلاحياته أضيق من صلاحيات
  // مؤشّري المؤسسة: صاحب الدرجة يقرأ درجته هو، والدرجات كلها (بلا ترتيب) لإدارة
  // المؤسسة وحدها. لا لوحة شرف ولا ترتيب (13 سبتمبر 2026) — الإنجازات في الأخبار.
  'excellence.view_own': ['SPECIALIST', 'COUNSELOR'],
  'excellence.scores.view': ['INSTITUTION_ADMIN'],
  'excellence.configure': ['INSTITUTION_ADMIN'],
  'excellence.compute': ['INSTITUTION_ADMIN'],
  'excellence.seed': ['PLATFORM_ADMIN'],

  // الجهة الرقابية — حسابٌ مستقل يشرف على كل ما تحته للقراءة (قرار 14 سبتمبر 2026).
  // `oversight.view` ليست لأي دور مؤسسة: يحملها المشرف الرقابي بصفته الشخصية
  // المشتقّة من `regulatory_supervisors`، لا بعضويةٍ في مؤسسة (ADR-007).
  // و`oversight.records.view` سجلاتُ ما تحتها (كادر، طلبة، خطط، جلسات، تقارير، تنبيهات) —
  // والنطاق (ربطٌ نشط + `full_oversight`) يُحسم في الخدمة بـ404.
  'oversight.view': ['REGULATORY_SUPERVISOR'],
  'oversight.records.view': ['REGULATORY_SUPERVISOR'],
  'regulatory_supervisor.manage': ['PLATFORM_ADMIN'],
  // المؤسسة تقرّر ما تشاركه فوق الحدّ النظامي — والحدّ نفسه لا يُنقص
  'regulatory_scope.manage': ['INSTITUTION_ADMIN'],

  // بثّ الفصول (P6) — البثّ يلتقط كل طفلٍ في الصفّ، فالمشاهدة أضيق ما في المنصة:
  // وليُّ أمرِ طالبٍ في روستر ذلك الصف وحده، والجهةُ الرقابية إن سمحت المؤسسة.
  // والنطاق يُفرض في الخدمة لا بالصلاحية — الصلاحية تفتح الباب، والخدمة تسأل «من أنت من هذا الصف».
  'broadcast.view': ['INSTITUTION_ADMIN', 'TEACHER'],
  'broadcast.configure': ['INSTITUTION_ADMIN'],
  'broadcast.manage': ['INSTITUTION_ADMIN', 'TEACHER'],
  // تسجيلُ الحصة (0092): الإدارةُ، ومن مُنح الصلاحيةَ فردياً من المعلّمين
  'broadcast.record': ['INSTITUTION_ADMIN', 'GRANT_CLASS_RECORDING'],
  // المشرف الرقابي ليس هنا: يشاهد عبر سطحه (`oversight.view`) لا عبر عضويةٍ لا يملكها
  'broadcast.watch': ['PARENT', 'INSTITUTION_ADMIN'],

  // الاشتراكات والفوترة (P6) — الباقات صفوف منصة، والاشتراك قرار إدارة المؤسسة
  'billing.plans.manage': ['PLATFORM_ADMIN', 'PLATFORM_FINANCE'],
  'billing.manage': ['INSTITUTION_ADMIN'],
  'billing.view': ['INSTITUTION_ADMIN'],

  // مركز المراجعة (P5) — طابور ما ينتظر دورك، مقيَّد بالدور والمسار في الخدمة
  'review.inbox': ['INTERNAL_SUPERVISOR', 'INSTITUTION_ADMIN'],
  // 1 نوفمبر 2026 — «المسودات» (0119): مسوداتُ الكادر في المؤسسة (من الأنواع التي يقرؤها السائلُ أصلاً) — للإدارة والمشرف والمفوَّض؛ و«مسوداتي» لكل من دخل بلا صلاحية
  'draft.institution.view': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR', 'INSTITUTION_DELEGATE'],
  // التوظيف (P6) — الإعلان للمؤسسة، والتقدُّم للحساب المهني، ولا بيانات اتصال تخرج قبل القبول
  'job.manage': ['INSTITUTION_ADMIN'],
  'job.apply': ['PROFESSIONAL'],
  // الظهور العلني اختياري صريح
  'institution.public_profile.manage': ['INSTITUTION_ADMIN'],
  // مقاعد الطلبة: المؤسسة تطلب زيادةً، وإدارة المنصة تعتمد فتُصدر الفاتورة
  'seat_request.create': ['INSTITUTION_ADMIN', 'INSTITUTION_DELEGATE'],
  'seat_request.view': ['INSTITUTION_ADMIN', 'INSTITUTION_DELEGATE'],
  'seat_request.decide': ['PLATFORM_ADMIN', 'PLATFORM_INSTITUTIONS'],
  // حزمة الاستمرارية (P6) — تُصدَر بموافقة مشاركة سارية وتُستورد برمز يحمله وليّ الأمر
  'continuity.issue': ['INSTITUTION_ADMIN'],
  'continuity.import': ['INSTITUTION_ADMIN'],
  'critical_incident.view': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR'],
  'critical_incident.handle': ['INSTITUTION_ADMIN'],

  // أخبار المنصة — تُنشر فيها الإنجازات بدل الترتيب؛ خبرُ المنصة من إدارتها وخبرُ المؤسسة من إدارتها
  'news.view': ['PLATFORM_ADMIN', 'INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR', 'SPECIALIST', 'COUNSELOR', 'TEACHER', 'OFFICE_STAFF'],
  // 22 سبتمبر 2026 — «أخبار مؤسستي» ليست للمعلّم (قرار صاحب المنصة)
  'news.view_institution': ['PARENT', 'STUDENT', 'INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR', 'SPECIALIST', 'COUNSELOR', 'OFFICE_STAFF'],
  'news.manage': ['PLATFORM_ADMIN', 'INSTITUTION_ADMIN'],
  // بنك الأهداف والاستراتيجيات — بنكُ المنصة من إدارتها، وبنكُ المؤسسة من إدارتها
  'goal_bank.view': ['PLATFORM_ADMIN', 'INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR', 'SPECIALIST', 'COUNSELOR'],
  // 8 أكتوبر 2026 — النماذجُ الرقمية (0080): القوالبُ يراها الجميع وتديرها الإدارة (والمفوَّض)، والإدخالُ لكل أدوار العضوية
  // بنطاق الطالب في الخدمة (`assertStaffLinked`)، وإعادةُ فتح النهائيّ للإدارة وحدها
  'form.template.view': ['PLATFORM_ADMIN', 'INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR', 'SPECIALIST', 'COUNSELOR', 'TEACHER', 'OFFICE_STAFF', 'INSTITUTION_DELEGATE'],
  'form.template.manage': ['PLATFORM_ADMIN', 'INSTITUTION_ADMIN', 'INSTITUTION_DELEGATE'],
  'form.entry.view': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR', 'SPECIALIST', 'COUNSELOR', 'TEACHER', 'OFFICE_STAFF', 'INSTITUTION_DELEGATE'],
  'form.entry.manage': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR', 'SPECIALIST', 'COUNSELOR', 'TEACHER', 'OFFICE_STAFF', 'INSTITUTION_DELEGATE'],
  'form.entry.reopen': ['INSTITUTION_ADMIN'],
  // 30 سبتمبر 2026: الأخصائي والمرشد يضيفان أهدافاً إلى بنك مؤسستهما (ويعدّلان ما أضافاه هما) — إدارةُ المؤسسة تعدّل الكل
  'goal_bank.manage': ['PLATFORM_ADMIN', 'INSTITUTION_ADMIN', 'SPECIALIST', 'COUNSELOR'],
  // التمارين — البنية فقط الآن: بنكٌ وإسنادٌ وإنجازٌ وقفلٌ أبوي
  'exercise.view': ['PLATFORM_ADMIN', 'INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR', 'SPECIALIST', 'COUNSELOR'],
  'exercise.manage': ['PLATFORM_ADMIN', 'INSTITUTION_ADMIN'],
  'exercise.assign': ['INSTITUTION_ADMIN', 'SPECIALIST', 'COUNSELOR'],
  'exercise.parental': ['PARENT'],
  // بوابة الطالب (P7): الطالب يلعب تمارينه، ومن يشرف عليه يقفلها أو يجدولها، ووليّ الأمر يقرأ
  'games.play': ['STUDENT'],
  'student.games.manage': ['INSTITUTION_ADMIN', 'SPECIALIST', 'COUNSELOR'],
  // 22 سبتمبر 2026 — اللعبُ المشترك للمعلّم أيضاً مع طلبة روستره (بتنبيه ملاءمة العمر في الواجهة)؛ الضبطُ يبقى لـ`manage`
  'student.games.coplay': ['INSTITUTION_ADMIN', 'SPECIALIST', 'COUNSELOR', 'TEACHER'],
  'student.games.view_own_children': ['PARENT'],
  // بوابة الطالب — الإصدار الثاني (14 سبتمبر 2026): مدرستي ورسائلي وواجباتي وحصصي للطالب وحده،
  // والكادر المُسنَد يكلّف ويراجع ويردّ — والنطاق (إسناد/روستر) يُحسم في `assertStaffLinked`
  'student.portal.view': ['STUDENT'],
  'student.assignment.manage': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR', 'SPECIALIST', 'COUNSELOR', 'TEACHER'],
  // 25 سبتمبر 2026 — سجلُّ الحضور اليومي (0062): يسجّل من يرى الطفلَ في يومه، ويقرأ المشرفُ الداخلي معه
  'attendance.record': ['INSTITUTION_ADMIN', 'TEACHER', 'SPECIALIST', 'COUNSELOR', 'GRANT_ATTENDANCE'],
  // ── 0070 — منظومةُ المدارس (30 سبتمبر 2026) ──
  // الصفوف والشُّعب ومعلّموها وجداولُ حصصها: الإدارةُ تنشئ وتُسند، والمعلّمُ يرى صفوفَه، والمشرفُ يقرأ
  'classroom.view': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR', 'TEACHER', 'SPECIALIST', 'COUNSELOR'],
  'classroom.manage': ['INSTITUTION_ADMIN'],
  'timetable.manage': ['INSTITUTION_ADMIN'],
  // رسالةٌ لأولياء أمور صفٍّ كامل — التواصلُ الرسمي بيد الإدارة
  'classroom.notify_guardians': ['INSTITUTION_ADMIN'],
  // 0072 — الأكاديميات والدرجات (30 سبتمبر 2026): الإعدادُ (الفصول والمواد ومكوّنات التقييم) للإدارة؛
  // الاختباراتُ والرصدُ لمعلّم المادة في صفّه والإدارة؛ والكشوفُ لمن يرى الصف. بطاقةُ الطالب لوليّ الأمر
  // بعد نشر نتائج الفصل، وللطالب من بوابته كذلك.
  'academics.manage': ['INSTITUTION_ADMIN'],
  'academics.view': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR', 'TEACHER', 'SPECIALIST', 'COUNSELOR'],
  'gradebook.enter': ['INSTITUTION_ADMIN', 'TEACHER'],
  'gradebook.view': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR', 'TEACHER'],
  // عدّادُ أوراق العمل المرسلة (لوحة المشرفين)
  'worksheet.stats.view': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR'],
  'attendance.view': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR', 'TEACHER', 'SPECIALIST', 'COUNSELOR', 'GRANT_ATTENDANCE'],
  // ── 0076 — خدمةُ الباص (5 أكتوبر 2026) ──
  // الإدارةُ تُنشئ المسارات وتضمّ الطلبة؛ والمرافقُ (أيُّ عضوٍ تسمّيه الإدارة على المسار) يسجّل الركوبَ والنزول من جواله —
  // والخدمةُ تضيّق `transport.record` على مرافق المسار وحده؛ والمشرفُ يقرأ. وليُّ الأمر يرى طفلَه من `student.view_own_children`.
  'transport.manage': ['INSTITUTION_ADMIN'],
  // 13 أكتوبر 2026 — والموظفُ الإداري أيضاً: «سائق ومرافق باص» مسمّى من فهرس الكادر الإداري، والتسجيلُ تضيّقه الخدمةُ على مرافق المسار
  'transport.view': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR', 'TEACHER', 'SPECIALIST', 'COUNSELOR', 'OFFICE_STAFF'],
  'transport.record': ['INSTITUTION_ADMIN', 'TEACHER', 'SPECIALIST', 'COUNSELOR', 'INTERNAL_SUPERVISOR', 'OFFICE_STAFF'],
  'student.message.reply': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR', 'SPECIALIST', 'COUNSELOR', 'TEACHER'],

  // مسار الإرشاد الخاص — المستند 05 §Counseling · BR-COUNSEL-001
  'counseling.plan.manage': ['COUNSELOR'],
  'counseling.session.manage': ['COUNSELOR'],
  'counseling.report.write': ['COUNSELOR'],
  // الإدارة والمشرف يقرآن علَم الإدارة وحده — الخدمة تُضيّق ما يعود
  'counseling.report.view': ['COUNSELOR', 'INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR'],
  // «المرشد فقط، ولطلبته المُسنَدين فقط» — والملكية تُفرض في الخدمة لا بالدور
  'counseling.private_note.view': ['COUNSELOR'],
  'counseling.recommendation.share': ['COUNSELOR'],
  'counseling.recommendation.view_management': ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR'],
  'counseling.recommendation.view_own': ['PARENT', 'STUDENT'],

  // الحساب المهني (صاحبه فقط — النطاق يُفرض بالخدمة)
  'professional.self.manage': ['PROFESSIONAL'],

  // ── 0031 — العميل والاستشارات المدفوعة والمحفظة (16 سبتمبر 2026) ──
  // النطاق يُضيَّق في الخدمة على المالك (عميلٌ يرى استشاراته، ومهنيٌّ يرى استشاراته)
  // لا هنا: المصفوفة تقول «من يدخل الباب»، والخدمة تقول «ماذا يرى خلفه».
  'client.self': ['CLIENT'],
  'client.manage': ['PLATFORM_ADMIN', 'PLATFORM_SUPPORT'],
  'professional.services.manage': ['PROFESSIONAL'],
  'consultation.provide': ['PROFESSIONAL'],
  // البابُ مشتركٌ والنطاقُ ليس كذلك: كلٌّ يرى استشاراتِه هو (تُفرض في الخدمة)
  'consultation.view': ['CLIENT', 'PROFESSIONAL'],
  'wallet.self.view': ['CLIENT', 'PROFESSIONAL'],
  'payout.request': ['PROFESSIONAL'],
  'payout.decide': ['PLATFORM_ADMIN', 'PLATFORM_FINANCE'],
  'complaint.review': ['PLATFORM_ADMIN', 'PLATFORM_SUPPORT'],
  'platform.pricing.manage': ['PLATFORM_ADMIN', 'PLATFORM_FINANCE'],
  // المالية (0117): الإيراداتُ والضريبةُ والذممُ والالتزاماتُ والمصروفات — للإدارة والمالية وحدهما
  'finance.view': ['PLATFORM_ADMIN', 'PLATFORM_FINANCE'],
  'finance.manage': ['PLATFORM_ADMIN', 'PLATFORM_FINANCE'],

  // ── 0032 — التسجيل الأكاديمي (مقدّم الدورات) ──
  'course_provider.apply': ['PROFESSIONAL'],
  // 20 أكتوبر 2026 — تفعيلُ الاستشارات بطلب (0105): المهنيُّ يقدّم، وإدارةُ المنصة تقرّر
  'consultant.apply': ['PROFESSIONAL'],
  'consultant.review': ['PLATFORM_ADMIN', 'PLATFORM_SUPPORT'],
  'course_provider.review': ['PLATFORM_ADMIN', 'PLATFORM_ACADEMIES'],

  // ── 0034 — تقويم الاستشاري (16 سبتمبر 2026) ──
  // من يملك خدمةً يملك تقويمها — فالبابُ نفسُه الذي يعرّف الخدمة يضبط أوقاتها
  'availability.manage': ['PROFESSIONAL'],
  // طلبُ تسجيل المركز يصل إدارة المنصة وحدها — ولا يراه مَن لم يُقبل بعد
  'institution_request.review': ['PLATFORM_ADMIN', 'PLATFORM_INSTITUTIONS'],

  // ── 0066 — أقسامُ إدارة المنصة (27 سبتمبر 2026) ──
  // مساعدةُ صاحب حسابٍ اتصل: فكُّ القفل والكلمةُ المؤقتة وإنهاءُ الجلسات — بلا تعديل الحساب ولا حذفه
  'user.assist': ['PLATFORM_ADMIN', 'PLATFORM_SUPPORT'],
  // ملاحظاتُ التحسين: القسمُ يكتب لنوع الهدف الذي يخصّه وحده (تُفرض في الخدمة)، والمال لا هدفَ له هنا
  'platform_note.manage': ['PLATFORM_ADMIN', 'PLATFORM_SUPPORT', 'PLATFORM_ACADEMIES', 'PLATFORM_INSTITUTIONS'],
  // صاحبُ الهدف يقرأ ملاحظاته ويردّ: إدارةُ المؤسسة لمؤسستها، والمهنيُّ لنفسه وأكاديميته ودوراته
  'platform_note.respond': ['INSTITUTION_ADMIN', 'PROFESSIONAL'],
  // كلُّ قسمٍ يقرأ سجلَّ أفعاله هو — لا السجلَّ كلَّه (`audit.view_all` لإدارة المنصة وحدها)
  'audit.view_self': ['PLATFORM_SUPPORT', 'PLATFORM_ACADEMIES', 'PLATFORM_INSTITUTIONS', 'PLATFORM_FINANCE'],
  // لوحةُ اليوم لإدارة المنصة وأقسامها — أرقامُ ما يخصّ كلَّ قسمٍ وحده
  'platform.dashboard': ['PLATFORM_ADMIN', 'PLATFORM_SUPPORT', 'PLATFORM_ACADEMIES', 'PLATFORM_INSTITUTIONS', 'PLATFORM_FINANCE'],

  // ── 0033 — الأكاديميات والدورات ──
  'academy.manage': ['PROFESSIONAL'],
  'academy.review': ['PLATFORM_ADMIN', 'PLATFORM_ACADEMIES'],
  // 1 نوفمبر 2026 — تعديلُ نصوص الأكاديمية من إدارة المنصة (0120): النصوصُ وحدها بسببٍ إلزامي
  'academy.content.edit': ['PLATFORM_ADMIN', 'PLATFORM_ACADEMIES'],
  'course.manage': ['PROFESSIONAL'],
  // يتدرّب العميلُ ووليُّ الأمر والمهني — والجمهور يُصفّى في الخدمة لا هنا
  'course.enroll': ['CLIENT', 'PARENT', 'PROFESSIONAL'],
  // ── 0057 — الأكاديمية مجموعةً (24 سبتمبر 2026) ──
  // الاشتراكُ في الأكاديمية وقراءةُ منشوراتها وشراؤها والتعليقُ: مَن يحقّ له التدرّبُ يحقّ له الانضمام
  'academy.subscribe': ['CLIENT', 'PARENT', 'PROFESSIONAL'],
} as const satisfies Record<string, readonly EffectiveRole[]>;

export type Permission = keyof typeof PERMISSIONS;

export function roleHasPermission(roles: EffectiveRole[], permission: Permission): boolean {
  const allowed = PERMISSIONS[permission] as readonly EffectiveRole[];
  return roles.some((r) => allowed.includes(r));
}

/** الأدوار التي تتطلب سياق مؤسسة (X-Institution-Id) */
export const TENANT_ROLES: EffectiveRole[] = ['INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR', 'SPECIALIST', 'COUNSELOR', 'TEACHER', 'OFFICE_STAFF', 'INSTITUTION_DELEGATE', 'GRANT_ATTENDANCE', 'GRANT_CLASS_RECORDING'];
